import { PaymentMethod } from "model/Gig";

/**
 * Normalize payment type values from mixed API/string inputs into known enum values.
 */
const normalizePaymentType = (
  value?: PaymentMethod | string | null,
): PaymentMethod | null => {
  if (!value) return null;
  if (value === PaymentMethod.SIMFLEXITY_PAY) return PaymentMethod.SIMFLEXITY_PAY;
  if (value === PaymentMethod.NONE) return PaymentMethod.NONE;
  if (value === "SimflexityPay") return PaymentMethod.SIMFLEXITY_PAY;
  if (value === "None") return PaymentMethod.NONE;
  return null;
};

/**
 * Resolve the cutoff day for the fourth-rule based on payment methods.
 */
export const getFourthRuleCutoffDay = (
  paymentTypes: Array<PaymentMethod | string | null | undefined>,
): number => {
  const normalized = paymentTypes
    .map(normalizePaymentType)
    .filter((value): value is PaymentMethod => Boolean(value));

  if (normalized.length === 0) return 4;
  const anySimflexityPay = normalized.includes(PaymentMethod.SIMFLEXITY_PAY);
  return anySimflexityPay ? 4 : 19;
};

/**
 * Get the earliest editable date (inclusive) based on the fourth-rule cutoff.
 */
export const getEarliestEditableDateByFourthRule = (
  paymentTypes: Array<PaymentMethod | string | null | undefined>,
  now: Date = new Date(),
): Date => {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  const cutoffDay = getFourthRuleCutoffDay(paymentTypes);
  const lockFrom = new Date(
    today.getFullYear(),
    today.getMonth(),
    cutoffDay + 1,
  );

  const usePreviousMonth = today < lockFrom;
  const monthOffset = usePreviousMonth ? -1 : 0;

  return new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
};

/**
 * Determine if a shift date is editable under the fourth-rule cutoff.
 */
export const canEditShiftByFourthRule = (
  shiftStartTime: string,
  paymentTypes: Array<PaymentMethod | string | null | undefined>,
  now: Date = new Date(),
): boolean => {
  const shiftDate = new Date(shiftStartTime);
  if (Number.isNaN(shiftDate.getTime())) return false;
  shiftDate.setHours(0, 0, 0, 0);

  const earliest = getEarliestEditableDateByFourthRule(paymentTypes, now);
  return shiftDate >= earliest;
};
